import { Head } from 'vite-react-ssg'
import { SITE } from '../lib/site'
import { useLocale, localizedHref, FR_ENABLED, HTML_LANG } from '../i18n/locale'

interface Props {
  title: string
  description: string
  path: string // English canonical path (e.g. /calculators/paint/); locale is derived from the URL
  schema?: object | object[]
  image?: string // Optional per-page social-share image; defaults to the sitewide OG image
}

// Default sitewide social-share image. 1200×630 PNG — drop the file at
// /public/og-image.png (it'll be served from /og-image.png on the live site).
const DEFAULT_OG_IMAGE = '/og-image.png'

export default function Seo({ title, description, path, schema, image }: Props) {
  const locale = useLocale()
  const canonical = SITE.url + localizedHref(path, locale)
  const hasFr = FR_ENABLED.has(path)
  const enUrl = SITE.url + path
  const frUrl = SITE.url + localizedHref(path, 'fr')
  // Absolute URL is required by both Open Graph and Twitter Card spec.
  const ogImage = image
    ? (image.startsWith('http') ? image : SITE.url + image)
    : SITE.url + DEFAULT_OG_IMAGE
  const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : []
  return (
    <Head>
      <html lang={HTML_LANG[locale]} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {hasFr && <link rel="alternate" hrefLang="en" href={enUrl} />}
      {hasFr && <link rel="alternate" hrefLang="fr-CA" href={frUrl} />}
      {hasFr && <link rel="alternate" hrefLang="x-default" href={enUrl} />}
      {/* Open Graph (Facebook, LinkedIn, Slack, iMessage, Discord, etc.) */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:locale" content={locale === 'fr' ? 'fr_CA' : 'en_US'} />
      {hasFr && <meta property="og:locale:alternate" content={locale === 'fr' ? 'en_US' : 'fr_CA'} />}
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      {/* Twitter / X — falls back to Open Graph but explicit tags render better */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Head>
  )
}

export function softwareAppSchema(name: string, description: string, url: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name,
    description,
    url: url.startsWith('http') ? url : SITE.url + url,
    applicationCategory: 'UtilitiesApplication',
    operatingSystem: 'Any',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  }
}
